import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import type { ReactNode } from 'react';
import interLatinWoff2 from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url';
import { useIsDark } from '@/hooks/ui/useIsDark';
import { getStripe } from '@/lib/stripe';

// Theme tokens passed to the PaymentElement so the embedded (cross-iframe)
// Stripe fields match our UI - our CSS can't reach inside the iframe, so
// colours are handed to Stripe's `appearance` API instead. Light is the
// original 'stripe' theme; dark uses Stripe's 'night' base retinted to our
// neutral-grey surfaces + warm off-white text + lavender --primary, so the
// inputs stop rendering as white boxes on the dark canvas.
const lightAppearance = {
  theme: 'stripe' as const,
  variables: {
    colorPrimary: '#111827',
    colorBackground: '#ffffff',
    colorText: '#111827',
    colorDanger: '#dc2626',
    fontFamily: '"Inter Variable", Inter, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '6px',
  },
};

/**
 * Naming a font in `appearance.variables` is not enough - the Element renders
 * in a cross-origin iframe that cannot see our stylesheet, so without this the
 * card fields silently fell back to system-ui. Handing Stripe the self-hosted
 * woff2 (Vite resolves it to a hashed same-origin asset) is what actually makes
 * the inputs render in Inter Variable. Latin subset only; the fields hold card
 * numbers and postal codes.
 */
const elementsFonts = [
  {
    family: 'Inter Variable',
    src: `url(${interLatinWoff2})`,
    weight: '400 700',
    display: 'swap' as const,
  },
];

const darkAppearance = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#ac8bf9', // --primary (lavender)
    colorBackground: '#26262b', // ~ --card, neutral grey
    colorText: '#e7e4df', // --foreground (warm off-white)
    colorTextSecondary: '#a4a4ad', // --muted-foreground (labels / placeholder)
    colorDanger: '#f87171',
    fontFamily: '"Inter Variable", Inter, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
  },
};

/**
 * A subscription the Elements are created for before any intent exists
 * (Stripe's "deferred intent"). The amount is what Apple Pay / Google Pay
 * show on their sheet - with a SetupIntent they showed USD 0.00.
 */
export interface DeferredSubscription {
  /** Today's charge, in cents (the first invoice, after any promo). */
  amountCents: number;
  currency: string;
}

type StripeProviderProps = {
  children: ReactNode;
} & (
  | {
      /** SetupIntent client_secret: saving a card with no charge. */
      clientSecret: string;
      subscription?: never;
    }
  | { subscription: DeferredSubscription; clientSecret?: never }
);

function elementsOptions(
  props: StripeProviderProps,
  isDark: boolean
): StripeElementsOptions {
  const shared = {
    appearance: isDark ? darkAppearance : lightAppearance,
    fonts: elementsFonts,
    loader: 'auto' as const,
  };
  if (props.subscription) {
    return {
      ...shared,
      mode: 'subscription',
      amount: props.subscription.amountCents,
      currency: props.subscription.currency,
      // Must match `payment_method_types` on the subscription the backend
      // creates (`StripeService.create_subscription`).
      paymentMethodTypes: ['card'],
    };
  }
  return { ...shared, clientSecret: props.clientSecret };
}

export function StripeProvider(props: StripeProviderProps) {
  const isDark = useIsDark();
  return (
    <Elements stripe={getStripe()} options={elementsOptions(props, isDark)}>
      {props.children}
    </Elements>
  );
}
